import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { syncApi } from '../api/sync.api';

const POLL_MS = 3000;

/**
 * Poll CRM sync status only — does not trigger sync on page refresh.
 * Scheduled sync runs every 15 minutes on the background worker.
 */
export function useSyncStatus() {
  const queryClient = useQueryClient();

  const { data: status, refetch } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => syncApi.getStatus(),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'running' ? POLL_MS : 30_000;
    },
  });

  const prevStatusRef = useRef<string | undefined>();

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status?.status;

    if (prev === 'running' && status?.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      queryClient.invalidateQueries({ queryKey: ['subscribers-summary'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  }, [status?.status, queryClient]);

  return {
    status,
    isSyncing: status?.status === 'running',
    refetchStatus: refetch,
  };
}

export default useSyncStatus;
